'use client';

import { useEffect, useRef } from 'react';

import { addDays, format } from 'date-fns';
import { usePathname } from 'next/navigation';

import { Reviews } from '@screens/Product/ui/MiddleTabs/Reviews/Reviews';
import { PRODUCT_IMAGE_PATH } from '@shared/config/paths/paths';
import {
  ID_ARTICLE_ATTRIBUTE,
  ID_BRAND_ATTRIBUTE,
  ID_COLOR_ATTRIBUTE,
  ID_DESCRIPTION_ATTRIBUTE,
  ID_FACADE_MATERIAL_ATTRIBUTE,
  ID_PRODUCT_IMAGE_ATTRIBUTE
} from '@shared/constants/keys';
import { useAppStore } from '@shared/hooks/redux';
import { CharacteristicCategoryType } from '@shared/types/characteristics';
import { Metrics } from '@shared/types/enums/metrics.enum';
import { BreadcrumbItemType } from '@shared/types/global';
import { ProductRatingType, ProductReviewsResponse, ProductType } from '@shared/types/product';
import { JsonLd } from '@shared/ui/JsonLd';
import { sendMetrics } from '@shared/utils/sendMetrics/sendMetrics';

import { initState } from '../model/slice/productSlice';
import { BlogPostType } from '../model/types/blog';
import { Intro } from './Intro/Intro';
import { Characteristics } from './MiddleTabs/Characteristics/Characteristics';
import { Description } from './MiddleTabs/Description/Description';
import { MiddleTabs } from './MiddleTabs/MiddleTabs';
import { Passport } from './MiddleTabs/Passport/Passport';
import { Photo } from './MiddleTabs/Photo/Photo';
import { Schemes } from './MiddleTabs/Schemes/Schemes';
import { Video } from './MiddleTabs/Video/Video';
import { ProductBlogPosts } from './ProductBlogPosts/ProductBlogPosts';
import cls from './Product.module.scss';

type ProductProps = {
  data: ProductType;
  breadcrumbs: BreadcrumbItemType[];
  productCharacteristics?: CharacteristicCategoryType;
  productRating?: ProductRatingType;
  productReviews?: ProductReviewsResponse;
  blogPosts?: BlogPostType[];
};

export const Product = ({
  data,
  breadcrumbs,
  productCharacteristics,
  productRating,
  productReviews,
  blogPosts
}: ProductProps) => {
  const store = useAppStore();
  const initialized = useRef(false);

  const pathname = usePathname();

  if (!initialized.current) {
    store.dispatch(initState({ data }));
    initialized.current = true;
  }

  useEffect(() => {
    sendMetrics(Metrics.VIEW_PRODUCT);
  }, []);

  return (
    <>
      <Intro
        breadcrumbs={breadcrumbs}
        rating={productRating}
      />
      <MiddleTabs
        tabs={[
          { value: 0, ariaLabel: 'Показать описание', name: 'Описание', tabPanel: <Description /> },
          { value: 1, ariaLabel: 'Показать Фото', name: 'Фото', tabPanel: <Photo /> },
          { value: 3, ariaLabel: 'Показать Видео', name: 'Видео', tabPanel: <Video /> },
          {
            value: 4,
            ariaLabel: 'Показать Характеристики',
            name: 'Характеристики',
            tabPanel: <Characteristics productCharacteristics={productCharacteristics} />
          },

          { value: 5, ariaLabel: 'Показать Схемы', name: 'Схемы', tabPanel: <Schemes /> },
          { value: 6, ariaLabel: 'Показать Паспорт', name: 'Паспорт', tabPanel: <Passport /> },
          {
            value: 7,
            ariaLabel: 'Показать Инструкции',
            name: 'Инструкции',
            tabPanel: <Passport isInstruction={true} />
          }
        ]}
      />
      <Reviews
        productId={data.id}
        productReviews={productReviews}
      />
      <ProductBlogPosts
        className={cls.blogPosts}
        posts={blogPosts}
      />

      <JsonLd
        html={{
          '@type': 'Product',
          name: data.name,
          image: data.props[ID_PRODUCT_IMAGE_ATTRIBUTE]?.values?.map(
            (image) => `${process.env.NEXT_PUBLIC_SITE_URL}${PRODUCT_IMAGE_PATH}/${image.value}/600.webp`
          ),
          description: data.props[ID_DESCRIPTION_ATTRIBUTE]?.values[0]?.value,
          sku: data.props[ID_ARTICLE_ATTRIBUTE]?.values[0]?.value,
          material: data.props[ID_FACADE_MATERIAL_ATTRIBUTE]?.values[0]?.value,
          color: data.props[ID_COLOR_ATTRIBUTE]?.values[0]?.value,
          productId: data.id,
          brand: {
            '@type': 'Brand',
            name: data.props[ID_BRAND_ATTRIBUTE]?.values[0]?.value
          },
          offers: {
            '@type': 'Offer',
            url: `${process.env.NEXT_PUBLIC_SITE_URL}${pathname}`,
            priceCurrency: 'RUB',
            price: data.retailPrice,
            priceValidUntil: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
            availability: 'https://schema.org/InStock'
          }
        }}
      />
    </>
  );
};
